import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Button, Input, Popover, Space } from "antd";

// ─── DividerBlot ─────────────────────────────────────────────────────────────
// Registers <hr> as a block-embed so Quill preserves it when loading/saving
// HTML. This is required for email template 3-column reconstruction: the parser
// joins column sections with <hr> separators and the assembly splits on them.
const BlockEmbed = Quill.import("blots/block/embed") as any;
class DividerBlot extends BlockEmbed {
  static blotName = "divider";
  static tagName = "hr";
}
Quill.register(DividerBlot, true);

// ─── Public ref interface ─────────────────────────────────────────────────────

export interface EditorQuillRef {
  /** Insert plain text at the last known cursor position (or end of doc). */
  insertTextAtCursor: (text: string) => void;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface EditorQuillProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  enableButtonInsert?: boolean;
  onFocus?: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const EditorQuill = forwardRef<EditorQuillRef, EditorQuillProps>(
  (
    { value, onChange, placeholder, enableButtonInsert = false, onFocus },
    ref,
  ) => {
    const quillRef = useRef<ReactQuill>(null);
    // Tracks the cursor/selection when the editor loses focus so that
    // clicking an external element (variable tag, Insert Button) can still
    // insert at the correct position.
    const lastSelectionRef = useRef<{ index: number; length: number } | null>(
      null,
    );

    useImperativeHandle(ref, () => ({
      insertTextAtCursor(text: string) {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;
        const sel = lastSelectionRef.current;
        const index =
          sel != null
            ? sel.index + sel.length
            : Math.max(0, quill.getLength() - 1);
        quill.focus();
        quill.insertText(index, text, "api" as any);
      },
    }));

    // ── Button-insert state ───────────────────────────────────────────────────
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [btnLabel, setBtnLabel] = useState("Click here");
    const [btnUrl, setBtnUrl] = useState("");
    const [btnBg, setBtnBg] = useState("#1677ff");
    const [btnColor, setBtnColor] = useState("#ffffff");

    const handleInsertButton = () => {
      if (!btnLabel.trim()) return;
      const href = btnUrl.trim() || "#";
      const style =
        `display:inline-block;background-color:${btnBg};color:${btnColor};` +
        `padding:10px 24px;border-radius:4px;text-decoration:none;` +
        `font-weight:600;font-family:Arial,sans-serif;font-size:14px;`;
      const buttonHtml =
        `<p style="text-align:center;margin:16px 0;">` +
        `<a href="${href}" target="_blank" rel="noopener noreferrer" style="${style}">` +
        `${btnLabel.trim()}` +
        `</a></p>`;
      const current = (value || "").replace(/<p><br\s*\/?><\/p>$/i, "");
      onChange(current + buttonHtml + "<p><br></p>");
      setPopoverOpen(false);
      setBtnLabel("Click here");
      setBtnUrl("");
      setBtnBg("#1677ff");
      setBtnColor("#ffffff");
    };

    // Live preview of the button as the user configures it
    const buttonPreview = (
      <div style={{ textAlign: "center", margin: "4px 0 8px" }}>
        <a
          href="#"
          onClick={(e) => e.preventDefault()}
          style={{
            display: "inline-block",
            background: btnBg,
            color: btnColor,
            padding: "8px 20px",
            borderRadius: 4,
            textDecoration: "none",
            fontWeight: 600,
            fontFamily: "Arial,sans-serif",
            fontSize: 13,
          }}
        >
          {btnLabel || "Button Label"}
        </a>
      </div>
    );

    return (
      <div>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={onChange}
          placeholder={placeholder || "Enter message content"}
          style={{ background: "#fff", borderRadius: "10px" }}
          onFocus={onFocus}
          onChangeSelection={(range) => {
            // range is null on blur — keep the last known position
            if (range) lastSelectionRef.current = range;
          }}
        />

        {/* {enableButtonInsert && (
          <div style={{ marginTop: 6, display: "flex", justifyContent: "flex-end" }}>
            <Popover
              open={popoverOpen}
              onOpenChange={setPopoverOpen}
              trigger="click"
              placement="bottomRight"
              title="Insert CTA Button"
              content={
                <Space direction="vertical" style={{ width: 260 }}>
                  {buttonPreview}
                  <div>
                    <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>
                      Button Label
                    </div>
                    <Input
                      placeholder='e.g. "Click here"'
                      value={btnLabel}
                      onChange={(e) => setBtnLabel(e.target.value)}
                      onPressEnter={handleInsertButton}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>
                      Button URL
                    </div>
                    <Input
                      placeholder="https://..."
                      value={btnUrl}
                      onChange={(e) => setBtnUrl(e.target.value)}
                      onPressEnter={handleInsertButton}
                    />
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>
                        Background
                      </div>
                      <input
                        type="color"
                        value={btnBg}
                        onChange={(e) => setBtnBg(e.target.value)}
                        style={{ width: "100%", height: 32, cursor: "pointer", border: "1px solid #d9d9d9", borderRadius: 4 }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>
                        Text Color
                      </div>
                      <input
                        type="color"
                        value={btnColor}
                        onChange={(e) => setBtnColor(e.target.value)}
                        style={{ width: "100%", height: 32, cursor: "pointer", border: "1px solid #d9d9d9", borderRadius: 4 }}
                      />
                    </div>
                  </div>
                  <Button type="primary" size="small" block onClick={handleInsertButton}>
                    Insert
                  </Button>
                </Space>
              }
            >
              <Button size="small">+ Insert Button</Button>
            </Popover>
          </div>
        )} */}
      </div>
    );
  },
);

EditorQuill.displayName = "EditorQuill";

export default EditorQuill;
